import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight, RefreshCw, Share2, Star, AlertTriangle, ExternalLink } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { convert, pairRate, COMMON_AMOUNTS } from "@/lib/calculators/currency";
import {
  getRates,
  getHistory,
  supportsHistory,
  type FxSnapshot,
  type HistoryPoint,
} from "@/lib/api/exchangeRates";

type B = { en: string; bn: string };
type Lang = "en" | "bn";

const CURRENCIES: { code: string; flag: string; name: B }[] = [
  { code: "GBP", flag: "🇬🇧", name: { en: "British Pound", bn: "ব্রিটিশ পাউন্ড" } },
  { code: "USD", flag: "🇺🇸", name: { en: "US Dollar", bn: "মার্কিন ডলার" } },
  { code: "BDT", flag: "🇧🇩", name: { en: "Bangladeshi Taka", bn: "বাংলাদেশি টাকা" } },
  { code: "EUR", flag: "🇪🇺", name: { en: "Euro", bn: "ইউরো" } },
  { code: "INR", flag: "🇮🇳", name: { en: "Indian Rupee", bn: "ভারতীয় রুপি" } },
  { code: "AED", flag: "🇦🇪", name: { en: "UAE Dirham", bn: "আমিরাতি দিরহাম" } },
  { code: "SAR", flag: "🇸🇦", name: { en: "Saudi Riyal", bn: "সৌদি রিয়াল" } },
  { code: "MYR", flag: "🇲🇾", name: { en: "Malaysian Ringgit", bn: "মালয়েশিয়ান রিঙ্গিত" } },
  { code: "SGD", flag: "🇸🇬", name: { en: "Singapore Dollar", bn: "সিঙ্গাপুর ডলার" } },
  { code: "JPY", flag: "🇯🇵", name: { en: "Japanese Yen", bn: "জাপানি ইয়েন" } },
  { code: "CAD", flag: "🇨🇦", name: { en: "Canadian Dollar", bn: "কানাডিয়ান ডলার" } },
  { code: "AUD", flag: "🇦🇺", name: { en: "Australian Dollar", bn: "অস্ট্রেলিয়ান ডলার" } },
  { code: "CNY", flag: "🇨🇳", name: { en: "Chinese Yuan", bn: "চীনা ইউয়ান" } },
  { code: "PKR", flag: "🇵🇰", name: { en: "Pakistani Rupee", bn: "পাকিস্তানি রুপি" } },
];

const QUICK_PAIRS: { from: string; to: string; label: string }[] = [
  { from: "GBP", to: "USD", label: "GBP → USD" },
  { from: "USD", to: "BDT", label: "USD → BDT" },
  { from: "GBP", to: "BDT", label: "GBP → BDT" },
  { from: "EUR", to: "BDT", label: "EUR → BDT" },
  { from: "INR", to: "BDT", label: "INR → BDT" },
  { from: "AED", to: "BDT", label: "AED → BDT" },
  { from: "SAR", to: "BDT", label: "SAR → BDT" },
  { from: "MYR", to: "BDT", label: "MYR → BDT" },
  { from: "SGD", to: "BDT", label: "SGD → BDT" },
];

const T = {
  h1: { en: "GBP to USD Converter", bn: "পাউন্ড থেকে ডলার কনভার্টার" },
  desc: {
    en: "Live British Pound to US Dollar conversion plus all major currencies to BDT — for freelancers, students, and remittance senders.",
    bn: "লাইভ পাউন্ড থেকে ডলার রূপান্তর — সাথে BDT এর সকল প্রধান কারেন্সি। ফ্রিল্যান্সার, ছাত্র ও রেমিট্যান্স প্রেরকদের জন্য।",
  },
  amount: { en: "Amount", bn: "পরিমাণ" },
  from: { en: "From", bn: "থেকে" },
  to: { en: "To", bn: "এ" },
  swap: { en: "Swap", bn: "অদলবদল" },
  refresh: { en: "Refresh", bn: "রিফ্রেশ" },
  updated: { en: "Rates updated", bn: "রেট আপডেট" },
  cached: {
    en: "Showing cached rates — couldn't reach the live API.",
    bn: "ক্যাশড রেট দেখানো হচ্ছে — লাইভ API তে পৌঁছানো যায়নি।",
  },
  fallback: {
    en: "Showing approximate fallback rates — live data unavailable.",
    bn: "আনুমানিক ফলব্যাক রেট দেখানো হচ্ছে — লাইভ ডেটা পাওয়া যায়নি।",
  },
  loading: { en: "Loading rates…", bn: "রেট লোড হচ্ছে…" },
  setDefault: { en: "Set as default pair", bn: "ডিফল্ট হিসেবে সংরক্ষণ" },
  share: { en: "Share", bn: "শেয়ার" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  common: { en: "Common amounts", bn: "সাধারণ পরিমাণ" },
  trend: { en: "30-day trend", bn: "৩০ দিনের প্রবণতা" },
  noChart: {
    en: "Historical chart unavailable for this pair (BDT not in ECB dataset).",
    bn: "এই জোড়ার জন্য চার্ট অনুপলব্ধ (BDT ECB ডেটাসেটে নেই)।",
  },
  remitNote: {
    en: "These are mid-market reference rates. bKash, banks, Western Union, Wise and other remittance providers apply their own margin — actual ৳ received will differ.",
    bn: "এগুলো মিড-মার্কেট রেফারেন্স রেট। bKash, ব্যাংক, ওয়েস্টার্ন ইউনিয়ন, Wise ও অন্যান্য রেমিট্যান্স প্রোভাইডার নিজস্ব মার্জিন প্রয়োগ করে — প্রকৃত প্রাপ্ত ৳ ভিন্ন হবে।",
  },
  bbRef: { en: "Bangladesh Bank reference", bn: "বাংলাদেশ ব্যাংক রেফারেন্স" },
  disclaimer: {
    en: "Rates shown are mid-market reference rates. Actual rates from banks, money exchanges, and remittance providers will differ. For real transactions, check with your provider.",
    bn: "প্রদর্শিত রেট মিড-মার্কেট রেফারেন্স রেট। ব্যাংক, মানি এক্সচেঞ্জ ও রেমিট্যান্স প্রোভাইডারের প্রকৃত রেট ভিন্ন হবে। প্রকৃত লেনদেনের জন্য আপনার প্রোভাইডারের সাথে যাচাই করুন।",
  },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "What is the GBP to USD rate today?", bn: "আজকের GBP to USD রেট কত?" },
    a: {
      en: "The live mid-market rate is shown at the top of this page and refreshed hourly. As a guide, 1 GBP typically equals 1.25–1.35 USD, but this fluctuates daily based on Bank of England and Federal Reserve policy, UK/US economic data, and global risk sentiment.",
      bn: "লাইভ মিড-মার্কেট রেট এই পেজের উপরে দেখানো আছে — প্রতি ঘণ্টায় রিফ্রেশ হয়। সাধারণত ১ GBP = ১.২৫–১.৩৫ USD, তবে এটি প্রতিদিন পরিবর্তিত হয়।",
    },
  },
  {
    q: { en: "How are exchange rates calculated?", bn: "এক্সচেঞ্জ রেট কীভাবে নির্ধারিত হয়?" },
    a: {
      en: "Major currencies trade in a global interbank market 24/5. The mid-market rate is the midpoint between the buy and sell prices banks quote each other. Retail customers always pay a margin above this — sometimes 1%, sometimes 5%+, depending on the provider.",
      bn: "প্রধান কারেন্সিগুলো ২৪/৫ গ্লোবাল ইন্টারব্যাংক মার্কেটে লেনদেন হয়। মিড-মার্কেট রেট হলো ব্যাংকগুলোর কেনা-বেচার দরের মাঝামাঝি। খুচরা গ্রাহক সবসময় এর উপরে ১–৫%+ মার্জিন দেন।",
    },
  },
  {
    q: { en: "Best way to convert GBP to BDT?", bn: "GBP থেকে BDT রূপান্তরের সেরা উপায়?" },
    a: {
      en: "Compare total ৳ received (not just the rate) across Wise, banks, bKash Remittance, Western Union, and MoneyGram. Government incentive of 2.5% on legal remittance through banking channels can offset margins. We do not recommend a specific provider — always compare on the day you send.",
      bn: "Wise, ব্যাংক, bKash রেমিট্যান্স, Western Union, MoneyGram-এ মোট প্রাপ্ত ৳ তুলনা করুন (শুধু রেট নয়)। বৈধ চ্যানেলে ২.৫% সরকারি প্রণোদনা মার্জিন কাটাতে সাহায্য করতে পারে। নির্দিষ্ট প্রোভাইডার সুপারিশ করা হয় না — পাঠানোর দিনে তুলনা করুন।",
    },
  },
  {
    q: { en: "GBP থেকে BDT কত?", bn: "GBP থেকে BDT কত?" },
    a: {
      en: "উপরের কনভার্টারে GBP → BDT সিলেক্ট করে পরিমাণ লিখুন। সাধারণত ১ GBP ≈ ১৪০–১৫৫ ৳, তবে রেট প্রতিদিন বদলায়। রেমিট্যান্স প্রোভাইডারের প্রকৃত রেট ১–৩% কম হতে পারে।",
      bn: "উপরের কনভার্টারে GBP → BDT সিলেক্ট করে পরিমাণ লিখুন। সাধারণত ১ GBP ≈ ১৪০–১৫৫ ৳, তবে রেট প্রতিদিন বদলায়। রেমিট্যান্স প্রোভাইডারের প্রকৃত রেট ১–৩% কম হতে পারে।",
    },
  },
  {
    q: { en: "ডলার থেকে টাকা রেট কত?", bn: "ডলার থেকে টাকা রেট কত?" },
    a: {
      en: "USD → BDT কনভার্টারে দেখুন। বাংলাদেশ ব্যাংকের রেফারেন্স রেট সাধারণত ১১৭–১২২ ৳/USD এর মধ্যে থাকে। খোলাবাজার (কার্ব মার্কেট) রেট এর চেয়ে বেশি হতে পারে।",
      bn: "USD → BDT কনভার্টারে দেখুন। বাংলাদেশ ব্যাংকের রেফারেন্স রেট সাধারণত ১১৭–১২২ ৳/USD এর মধ্যে থাকে। খোলাবাজার রেট এর চেয়ে বেশি হতে পারে।",
    },
  },
  {
    q: { en: "Why is the bank rate different from this rate?", bn: "ব্যাংকের রেট ভিন্ন কেন?" },
    a: {
      en: "Banks add a spread (margin) for risk and profit. Card networks (Visa/Mastercard) add ~1% on top of the interbank rate, plus your bank may add 2–3% as a foreign-currency fee. Cash exchanges typically have the worst rates.",
      bn: "ব্যাংক স্প্রেড (মার্জিন) যোগ করে। Visa/Mastercard ইন্টারব্যাংক রেটের উপর ~১% যোগ করে, এবং আপনার ব্যাংক আরও ২–৩% ফরেন কারেন্সি ফি নিতে পারে। নগদ এক্সচেঞ্জের রেট সাধারণত সবচেয়ে খারাপ।",
    },
  },
  {
    q: { en: "I'm a freelancer receiving USD via Payoneer/Wise — how does this affect me?", bn: "ফ্রিল্যান্সার হিসেবে USD/GBP পেলে কীভাবে প্রভাব ফেলে?" },
    a: {
      en: "Your take-home in ৳ depends on (1) the platform's conversion rate, (2) any withdrawal fee, and (3) the bank rate when funds settle. Payoneer's local-bank withdrawal often gives a slightly weaker rate than Wise. Check the per-USD ৳ received over a few months to compare.",
      bn: "আপনার প্রাপ্ত ৳ নির্ভর করে (১) প্ল্যাটফর্মের রূপান্তর রেট, (২) উইথড্র ফি, ও (৩) সেটেলমেন্টের সময় ব্যাংক রেটের উপর। Payoneer-এর লোকাল ব্যাংক উইথড্র সাধারণত Wise-এর চেয়ে সামান্য কম দেয়। কয়েক মাসের প্রতি USD ৳ তুলনা করুন।",
    },
  },
  {
    q: { en: "How often does this calculator update?", bn: "এই ক্যালকুলেটর কত ঘন ঘন আপডেট হয়?" },
    a: {
      en: "Live rates are fetched once per hour and cached locally in your browser. Use the Refresh button for the latest rate. If our rate source is unavailable, we fall back to the most recent cached value with a notice.",
      bn: "লাইভ রেট প্রতি ঘণ্টায় আপডেট হয় ও আপনার ব্রাউজারে ক্যাশ থাকে। সর্বশেষ রেটের জন্য Refresh চাপুন। সোর্স অনুপলব্ধ হলে আমরা সর্বশেষ ক্যাশড মান নোটিশসহ দেখাই।",
    },
  },
];

export const Route = createFileRoute("/gbp-to-usd")({
  head: () => {
    const seo = buildSeo({
      title: "GBP to USD Converter — Pound to Dollar Live Rate",
      description:
        "Live GBP to USD currency converter. Pound to Dollar exchange rate, also USD/GBP/EUR to BDT. Free, accurate, updated hourly.",
      path: "/gbp-to-usd",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "GBP to USD", path: "/gbp-to-usd" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: GbpToUsdPage,
});

const DEFAULT_KEY = "bc.fxDefaultPair.v1";

function GbpToUsdPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const [from, setFrom] = useState("GBP");
  const [to, setTo] = useState("USD");
  const [amountStr, setAmountStr] = useState("100");

  const [snap, setSnap] = useState<FxSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryPoint[] | null>(null);

  // Restore default pair from localStorage on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(DEFAULT_KEY);
      if (saved) {
        const { from: f, to: t } = JSON.parse(saved) as { from: string; to: string };
        if (f && t) {
          setFrom(f);
          setTo(t);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Load rates
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRates().then((s) => {
      if (!cancelled) {
        setSnap(s);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load history when pair changes
  useEffect(() => {
    let cancelled = false;
    setHistory(null);
    getHistory(from, to, 30).then((h) => {
      if (!cancelled) setHistory(h);
    });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));

  const amount = parseFloat(amountStr) || 0;
  const rates = snap?.rates;

  const result = useMemo(() => {
    if (!rates) return NaN;
    return convert(amount, from, to, rates);
  }, [amount, from, to, rates]);

  const oneRate = useMemo(() => (rates ? pairRate(from, to, rates) : NaN), [from, to, rates]);
  const reverseRate = useMemo(() => (rates ? pairRate(to, from, rates) : NaN), [from, to, rates]);

  const fmtMoney = (v: number, dp = 2) => {
    if (!Number.isFinite(v)) return "—";
    const s = v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const refresh = async () => {
    setLoading(true);
    const s = await getRates(true);
    setSnap(s);
    setLoading(false);
    const h = await getHistory(from, to, 30);
    setHistory(h);
  };

  const setDefault = () => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(DEFAULT_KEY, JSON.stringify({ from, to }));
    } catch {
      /* ignore */
    }
  };

  const share = async () => {
    const text = `${num(amount)} ${from} = ${fmtMoney(result)} ${to}`;
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: "Currency Converter",
          text,
          url,
        });
        return;
      } catch {
        /* fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} — ${url}`);
    } catch {
      /* ignore */
    }
  };

  const fromMeta = CURRENCIES.find((c) => c.code === from);
  const toMeta = CURRENCIES.find((c) => c.code === to);

  const updatedAgo = snap
    ? formatRelative(Date.now() - snap.fetchedAt, lang)
    : "";

  return (
    <CalculatorLayout
      slug="gbp-to-usd"
      title={tr("h1")}
      description={tr("desc")}
      iconName="DollarSign"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Status banner */}
      {snap?.source === "fallback" && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span className={cn(lang === "bn" && "font-bn")}>{tr("fallback")}</span>
        </div>
      )}
      {snap?.source === "cache" && Date.now() - snap.fetchedAt > 60 * 60 * 1000 && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span className={cn(lang === "bn" && "font-bn")}>{tr("cached")}</span>
        </div>
      )}

      {/* Quick pair chips */}
      <div className="space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Quick pairs" : "জনপ্রিয় জোড়া"}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_PAIRS.map((p) => {
            const active = p.from === from && p.to === to;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setFrom(p.from);
                  setTo(p.to);
                }}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-accent/40",
                )}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Converter */}
      <div className="mt-6 grid items-end gap-3 md:grid-cols-[1fr_auto_1fr]">
        <CurrencyPicker label={tr("from")} value={from} onChange={setFrom} lang={lang} />
        <button
          type="button"
          onClick={swap}
          aria-label={tr("swap")}
          className="mx-auto rounded-full border border-border bg-card p-2.5 transition hover:border-accent hover:text-accent active:rotate-180"
          style={{ transition: "transform 0.4s ease, border-color 0.2s, color 0.2s" }}
        >
          <ArrowLeftRight size={18} />
        </button>
        <CurrencyPicker label={tr("to")} value={to} onChange={setTo} lang={lang} />
      </div>

      <div className="mt-4">
        <NumberInput label={tr("amount")} value={amountStr} onChange={setAmountStr} unit={from} />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="gap-1.5">
            <RefreshCw size={14} className={cn(loading && "animate-spin")} />
            {tr("refresh")}
          </Button>
          <Button variant="outline" size="sm" onClick={setDefault} className="gap-1.5">
            <Star size={14} /> {tr("setDefault")}
          </Button>
          <Button variant="outline" size="sm" onClick={share} className="gap-1.5">
            <Share2 size={14} /> {tr("share")}
          </Button>
        </div>
      </div>

      {/* Result */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center"
        key={`${from}-${to}-${amount}`}
      >
        <p className={cn("text-xs font-semibold uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {fromMeta?.flag} {num(amount)} {from} =
        </p>
        <p className="mt-2 text-3xl font-bold tracking-tight tabular-nums md:text-5xl">
          {fmtMoney(result, to === "JPY" || to === "KRW" ? 0 : 2)}{" "}
          <span className="text-2xl text-muted-foreground md:text-3xl">{to}</span>
        </p>
        <p className={cn("mt-2 text-sm text-muted-foreground tabular-nums", lang === "bn" && "font-bn")}>
          1 {from} = {fmtMoney(oneRate, 4)} {to} &nbsp;·&nbsp; 1 {to} = {fmtMoney(reverseRate, 4)} {from}
        </p>
        {snap && (
          <p className={cn("mt-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("updated")}: {updatedAgo}
            {snap.source === "fallback" && ` (${lang === "en" ? "fallback" : "ফলব্যাক"})`}
          </p>
        )}
      </motion.div>

      {/* BDT remittance note */}
      {(from === "BDT" || to === "BDT") && (
        <div className="mt-4 rounded-md border border-border bg-card/50 p-3 text-xs text-muted-foreground">
          <p className={cn("font-medium text-foreground", lang === "bn" && "font-bn")}>{tr("bbRef")}</p>
          <p className={cn("mt-1", lang === "bn" && "font-bn")}>{tr("remitNote")}</p>
        </div>
      )}

      {/* Common amounts + Chart */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("common")}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{from}</th>
                <th className="px-3 py-2 text-right">{to}</th>
              </tr>
            </thead>
            <tbody>
              {COMMON_AMOUNTS.map((a) => (
                <tr key={a} className="border-t border-border">
                  <td className="px-3 py-2 tabular-nums">{fmtMoney(a, 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {rates ? fmtMoney(convert(a, from, to, rates), 2) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>
              {tr("trend")} — {from}/{to}
            </h3>
          </div>
          <div className="p-3" style={{ height: 240 }}>
            {!supportsHistory(from, to) ? (
              <p className={cn("flex h-full items-center justify-center px-4 text-center text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("noChart")}
              </p>
            ) : history && history.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(d: string) => d.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} width={50} />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(v: number) => v.toFixed(4)}
                  />
                  <Line type="monotone" dataKey="rate" stroke="hsl(var(--accent))" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className={cn("flex h-full items-center justify-center text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("loading")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
        <p className={cn("font-medium text-foreground", lang === "bn" && "font-bn")}>
          ⚠️ {lang === "en" ? "Disclaimer" : "দাবিত্যাগ"}
        </p>
        <p className={cn("mt-1", lang === "bn" && "font-bn")}>{tr("disclaimer")}</p>
      </div>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "About GBP and USD" : "GBP ও USD সম্পর্কে"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "The British Pound (GBP, £) is the world's oldest continuously used currency, issued by the Bank of England. The US Dollar (USD, $) is the global reserve currency, used in ~60% of international transactions. The GBP/USD pair (\"Cable\") is one of the most-traded forex pairs."
              : "ব্রিটিশ পাউন্ড (GBP, £) বিশ্বের প্রাচীনতম ধারাবাহিকভাবে ব্যবহৃত মুদ্রা — Bank of England ইস্যু করে। মার্কিন ডলার (USD, $) বৈশ্বিক রিজার্ভ কারেন্সি, প্রায় ৬০% আন্তর্জাতিক লেনদেনে ব্যবহৃত। GBP/USD জোড়া (\"Cable\") সবচেয়ে বেশি লেনদেন হওয়া ফরেক্স জোড়াগুলোর একটি।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Mid-market vs retail vs remittance rates" : "মিড-মার্কেট, খুচরা ও রেমিট্যান্স রেট"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li><strong>{lang === "en" ? "Mid-market" : "মিড-মার্কেট"}:</strong> {lang === "en" ? "the rate banks trade with each other — what we show here." : "ব্যাংকগুলো একে অপরের সাথে যে রেটে লেনদেন করে — আমরা এটাই দেখাই।"}</li>
            <li><strong>{lang === "en" ? "Retail" : "খুচরা"}:</strong> {lang === "en" ? "your bank or card issuer adds 1–4% margin on top." : "আপনার ব্যাংক বা কার্ড ইস্যুয়ার এর উপর ১–৪% মার্জিন যোগ করে।"}</li>
            <li><strong>{lang === "en" ? "Remittance" : "রেমিট্যান্স"}:</strong> {lang === "en" ? "providers like Western Union, MoneyGram, Wise, and bKash Remittance vary widely — sometimes better than banks, sometimes worse. Always compare total ৳ received." : "Western Union, MoneyGram, Wise, bKash Remittance — ব্যাপক তারতম্য। কখনো ব্যাংকের চেয়ে ভালো, কখনো খারাপ। মোট প্রাপ্ত ৳ তুলনা করুন।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Sending money to Bangladesh from UK/US" : "যুক্তরাজ্য/মার্কিন থেকে বাংলাদেশে টাকা পাঠানো"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Common channels include Wise (transparent mid-market rate + flat fee), bKash Remittance partners (instant to mobile wallet), Western Union/MoneyGram (cash pickup), and bank wire (slowest, often pricier). Bangladesh offers a 2.5% government incentive on legal inward remittance through banking channels — this can offset the provider margin. Always compare the final ৳ figure rather than the headline rate."
              : "সাধারণ চ্যানেল: Wise (স্বচ্ছ মিড-মার্কেট রেট + ফ্ল্যাট ফি), bKash রেমিট্যান্স পার্টনার (মোবাইল ওয়ালেটে তাৎক্ষণিক), Western Union/MoneyGram (নগদ পিকআপ), ব্যাংক ওয়্যার (ধীর, প্রায়ই ব্যয়বহুল)। বাংলাদেশ ব্যাংকিং চ্যানেলে বৈধ রেমিট্যান্সে ২.৫% সরকারি প্রণোদনা দেয় — এটি প্রোভাইডার মার্জিন পুষিয়ে দিতে পারে। হেডলাইন রেট নয়, চূড়ান্ত ৳ অংক তুলনা করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Best practices for getting good rates" : "ভালো রেট পাওয়ার অনুশীলন"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Compare 2–3 providers before each transfer — rates differ daily." : "প্রতিবার ২–৩টি প্রোভাইডার তুলনা করুন — রেট প্রতিদিন বদলায়।"}</li>
            <li>{lang === "en" ? "Avoid airport currency kiosks and hotel exchanges." : "এয়ারপোর্ট কিয়স্ক ও হোটেল এক্সচেঞ্জ এড়িয়ে চলুন।"}</li>
            <li>{lang === "en" ? "For card spending abroad, use a card with no FX markup." : "বিদেশে কার্ড খরচের জন্য FX মার্কআপ-মুক্ত কার্ড ব্যবহার করুন।"}</li>
            <li>{lang === "en" ? "For large remittances, time the transfer when the rate moves in your favour." : "বড় রেমিট্যান্সের জন্য অনুকূল রেটের সময় পাঠান।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "For BD freelancers receiving USD/GBP" : "USD/GBP প্রাপ্ত BD ফ্রিল্যান্সারদের জন্য"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Payoneer's local-bank withdrawal in BDT is convenient but typically gives a slightly weaker rate than Wise. Some banks offer dedicated freelancer accounts (e.g., ERQ accounts) that let you hold USD and convert when rates are favourable. Track your effective ৳/USD over time — the gap between platforms can add up to thousands of ৳ per month for a full-time freelancer."
              : "Payoneer-এর লোকাল ব্যাংক BDT উইথড্র সুবিধাজনক কিন্তু সাধারণত Wise-এর চেয়ে সামান্য কম রেট দেয়। কিছু ব্যাংক ফ্রিল্যান্সার অ্যাকাউন্ট (যেমন ERQ) দেয় — USD ধরে রেখে অনুকূল রেটে রূপান্তর করতে পারেন। আপনার কার্যকর ৳/USD ট্র্যাক করুন — প্ল্যাটফর্মের ব্যবধান মাসে হাজার ৳ যোগ করতে পারে।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Currency code reference (ISO 4217)" : "কারেন্সি কোড রেফারেন্স (ISO 4217)"} lang={lang}>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Currency</th>
                  <th className="px-3 py-2 text-left">Symbol</th>
                </tr>
              </thead>
              <tbody>
                {CURRENCIES.map((c) => (
                  <tr key={c.code} className="border-t border-border">
                    <td className="px-3 py-2 font-mono">{c.code}</td>
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{c.flag} {c.name[lang]}</td>
                    <td className="px-3 py-2">{symbolOf(c.code)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Income Tax" : "আয়কর"}
            </Link>
            <Link to="/discount-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Discount" : "ডিসকাউন্ট"}
            </Link>
            <Link to="/vat-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">VAT</Link>
          </div>
        </Section>

        <p className="text-xs text-muted-foreground">
          {lang === "en" ? "Data source: " : "ডেটা সোর্স: "}
          <a href="https://www.exchangerate-api.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
            exchangerate-api.com <ExternalLink size={11} />
          </a>
          {" · "}
          <a href="https://www.frankfurter.app/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
            Frankfurter (ECB) <ExternalLink size={11} />
          </a>
        </p>
      </article>
    </CalculatorLayout>
  );
}

function CurrencyPicker({
  label,
  value,
  onChange,
  lang,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  lang: Lang;
}) {
  const meta = CURRENCIES.find((c) => c.code === value);
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg">
          {meta?.flag ?? "🏳"}
        </span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-lg border border-border bg-muted py-2.5 pl-10 pr-3 text-base outline-none transition focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
        >
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} — {c.name[lang]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

function symbolOf(code: string): string {
  const map: Record<string, string> = {
    USD: "$", GBP: "£", EUR: "€", BDT: "৳", INR: "₹", AED: "د.إ", SAR: "﷼",
    MYR: "RM", SGD: "S$", JPY: "¥", CAD: "C$", AUD: "A$", CNY: "¥", PKR: "₨",
  };
  return map[code] ?? code;
}

function formatRelative(ms: number, lang: Lang): string {
  const sec = Math.max(1, Math.floor(ms / 1000));
  if (sec < 60) return lang === "en" ? `${sec}s ago` : `${toBengaliNumerals(sec)} সেকেন্ড আগে`;
  const min = Math.floor(sec / 60);
  if (min < 60) return lang === "en" ? `${min} min ago` : `${toBengaliNumerals(min)} মিনিট আগে`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return lang === "en" ? `${hr}h ago` : `${toBengaliNumerals(hr)} ঘণ্টা আগে`;
  const day = Math.floor(hr / 24);
  return lang === "en" ? `${day}d ago` : `${toBengaliNumerals(day)} দিন আগে`;
}
